/**
 * Exterior staircases and balconies. Owned by `world`.
 *
 * THE Montreal signature. A triplex is not a triplex without a painted steel stair running
 * from the sidewalk up to a first-floor balcony, and the reference photographs
 * (reference/montreal/Montreal_Plateau.JPG, Rue_Saint-Denis_Montreal_07.jpg,
 * Ruelle_de_Montr_al_2010.jpg) show three families of them:
 *
 *   · a long straight run parallel to the facade, ~20 shallow risers
 *   · the same with a quarter-turn flare at the foot, so the bottom steps face the street
 *   · a tight helix around a central post — the red ones in the ruelle photo
 *
 * Each variant is built ONCE as a merged geometry (treads + stringers + handrails +
 * balusters) and drawn as a single InstancedMesh with a per-instance paint colour, so all
 * ~550 staircases in the city cost five draw calls.
 *
 * Local frame: origin at the ground under the foot of the stair, +Y up, +Z pointing AWAY
 * from the facade (toward the street), +X running along the facade.
 */

import * as THREE from 'three';
import { MeshBuilder, FACE, trsE } from './geom.js';

/** Rise the variants are authored at. Instances scale Y by (actualRise / NOMINAL_RISE). */
export const NOMINAL_RISE = 3.74;

const _m = new THREE.Matrix4();

/** A box spanning a→b with cross-section w×h, oriented along the segment. */
function segBox(mb, ax, ay, az, bx, by, bz, w, h, faces = FACE.ALL) {
  const dx = bx - ax, dy = by - ay, dz = bz - az;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (len < 1e-5) return;
  const fx = dx / len, fy = dy / len, fz = dz / len;
  let ux = 0, uy = 1, uz = 0;
  if (Math.abs(fy) > 0.985) { ux = 1; uy = 0; uz = 0; }
  let rx = uy * fz - uz * fy, ry = uz * fx - ux * fz, rz = ux * fy - uy * fx;
  const rl = Math.sqrt(rx * rx + ry * ry + rz * rz) || 1;
  rx /= rl; ry /= rl; rz /= rl;
  const vx = fy * rz - fz * ry, vy = fz * rx - fx * rz, vz = fx * ry - fy * rx;
  _m.set(
    rx * w, vx * h, fx * len, (ax + bx) * 0.5,
    ry * w, vy * h, fy * len, (ay + by) * 0.5,
    rz * w, vz * h, fz * len, (az + bz) * 0.5,
    0, 0, 0, 1
  );
  mb.boxT(_m, faces);
}

/**
 * Tread path. Returns {x, z, yaw} at parameter t ∈ [0, N]; yaw is the heading of ascent so
 * that the ascent direction is (sin yaw, 0, cos yaw).
 */
function makePath(kind, N, st) {
  const run = st.treadRun;
  const zLine = st.standOff + st.width * 0.5;
  if (kind === 'straight') {
    return (t) => ({ x: t * run, z: zLine, yaw: Math.PI * 0.5 });
  }
  if (kind === 'curve') {
    const R = st.curveR, Nc = st.curveTreads;
    const cx = R, cz = zLine + R;
    return (t) => {
      if (t <= Nc) {
        const phi = -Math.PI + (t / Nc) * (Math.PI * 0.5);
        return { x: cx + Math.cos(phi) * R, z: cz + Math.sin(phi) * R, yaw: -phi };
      }
      return { x: cx + (t - Nc) * run, z: zLine, yaw: Math.PI * 0.5 };
    };
  }
  // helix
  const R = st.spiralR, sweep = st.spiralSweep;
  const cx = 0, cz = R + 0.34;
  const phiEnd = -Math.PI * 0.5;
  const phiStart = phiEnd - sweep;
  return (t) => {
    const phi = phiStart + (t / N) * sweep;
    return { x: cx + Math.cos(phi) * R, z: cz + Math.sin(phi) * R, yaw: -phi };
  };
}

/**
 * @param {boolean} coarse  build the FAR level of detail (see geom.js LodPair).
 *
 * A staircase is 400–1100 triangles at full detail and there are ~230 of them; past ~70 m the
 * balusters and the mid-rail are sub-pixel and the handrail's per-tread faceting is invisible.
 * The coarse build keeps everything that survives at distance — treads, stringers, top rail,
 * newels — and decimates everything that does not. Silhouette and value are unchanged.
 */
function buildOne(T, kind, mirror, coarse = false) {
  const st = T.stairs;
  const L = T.lod;
  const rise = NOMINAL_RISE;
  const N = Math.max(9, Math.round(rise / 0.235));
  const riser = rise / N;
  const path = makePath(kind, N, st);
  const mb = new MeshBuilder(false);

  const half = st.width * 0.5;
  const inner = half - st.stringerThick * 0.6;

  const pt = (t) => {
    const p = path(t);
    if (mirror) { p.x = -p.x; p.yaw = -p.yaw; }
    return p;
  };

  // --- treads ---------------------------------------------------------------
  for (let i = 1; i <= N; i++) {
    const p = pt(i - 0.5);
    const y = i * riser - st.treadThick * 0.5;
    const stretch = kind === 'helix' ? 1.7 : (kind === 'curve' && i <= st.curveTreads ? 1.9 : 1.15);
    mb.boxT(trsE(p.x, y, p.z, 0, p.yaw, 0, st.width, st.treadThick, st.treadRun * stretch), FACE.NO_BOTTOM);
  }

  // Collapse the path into straight runs so a plain flight is ONE rail box, not twenty.
  // A helix turns at EVERY tread, so a full-detail helix is N runs × 3 rails × 2 sides = 96
  // boxes; the coarse build chords the curve every `railChord` treads instead.
  const runs = [];
  let s0 = 0;
  const chord = coarse ? Math.max(1, L.stairRailChord) : 1;
  for (let i = 1; i <= N; i++) {
    const turned = i < N && Math.abs(pt(i).yaw - pt(i - 1).yaw) > 1e-6;
    if (i === N || (turned && (i - s0) >= chord)) { runs.push([s0, i]); s0 = i; }
  }

  const offAt = (i, lateral) => {
    const v = pt(i);
    return { x: v.x + Math.cos(v.yaw) * lateral, z: v.z - Math.sin(v.yaw) * lateral };
  };

  // --- stringers, handrails -------------------------------------------------
  for (const s of [-1, 1]) {
    for (const [i0, i1] of runs) {
      const a = offAt(i0, s * inner), b = offAt(i1, s * inner);
      segBox(mb, a.x, i0 * riser - st.stringerDepth * 0.5, a.z,
        b.x, i1 * riser - st.stringerDepth * 0.5, b.z,
        st.stringerThick, st.stringerDepth, FACE.SIDES);
      const ra = offAt(i0, s * half), rb = offAt(i1, s * half);
      segBox(mb, ra.x, i0 * riser + st.railH, ra.z, rb.x, i1 * riser + st.railH, rb.z,
        st.railThick, st.railThick, FACE.SIDES);
      if (!coarse) {
        segBox(mb, ra.x, i0 * riser + st.railH * 0.47, ra.z, rb.x, i1 * riser + st.railH * 0.47, rb.z,
          st.railThick * 0.75, st.railThick * 0.75, FACE.SIDES);
      }
    }
    // --- balusters ----------------------------------------------------------
    const balStep = Math.max(1, Math.round(st.balusterEvery / st.treadRun)) * (coarse ? L.stairBalusterSkip : 1);
    for (let i = 0; i < N; i += balStep) {
      const o = offAt(i, s * half);
      segBox(mb, o.x, i * riser, o.z, o.x, i * riser + st.railH, o.z,
        st.balusterThick, st.balusterThick, FACE.SIDES);
    }
    // newel posts, bottom and top
    const p0 = offAt(0, s * half), p1 = offAt(N, s * half);
    segBox(mb, p0.x, -0.05, p0.z, p0.x, st.railH + 0.10, p0.z, st.balusterThick * 2.2, st.balusterThick * 2.2, FACE.SIDES);
    segBox(mb, p1.x, rise - riser, p1.z, p1.x, rise + st.railH + 0.10, p1.z, st.balusterThick * 2.2, st.balusterThick * 2.2, FACE.SIDES);
  }

  // --- support legs / central post -----------------------------------------
  if (coarse && kind !== 'helix') {
    // legs are 8 tris each and read as nothing at range
  } else if (kind === 'helix') {
    // The helix axis: makePath puts it at (0, spiralR + 0.34); mirroring leaves x at 0.
    mb.cylinder(0, 0, st.spiralR + 0.34, 0.085, 0.085, rise + st.railH, 6, false, 1);
  } else {
    for (const f of [0.35, 0.62, 0.88]) {
      const t = Math.round(N * f);
      const p = pt(t);
      const y = t * riser - st.stringerDepth;
      if (y <= 0.3) continue;
      segBox(mb, p.x, 0, p.z, p.x, y, p.z, 0.08, 0.08, FACE.SIDES);
    }
  }

  const geo = mb.toGeometry('stair:' + kind + (mirror ? ':L' : ':R') + (coarse ? ':far' : ''));
  const p0 = pt(0), pN = pt(N);
  return {
    kind, mirror, geometry: geo, rise,
    /** Where the top tread lands, in local space — the balcony must meet this. */
    topX: pN.x, topZ: pN.z,
    footX: p0.x, footZ: p0.z,
    /** Extent along the facade, so a lot can decide whether it fits. */
    runLength: Math.abs(pN.x - p0.x) + st.width,
    depth: Math.max(p0.z, pN.z) + st.width * 0.5,
    tris: mb.triangleCount,
  };
}

/** Five variants: straight L/R, quarter-turn L/R, helix. `coarse` builds the far LOD. */
export function buildStairVariants(T, coarse = false) {
  return [
    buildOne(T, 'straight', false, coarse),
    buildOne(T, 'straight', true, coarse),
    buildOne(T, 'curve', false, coarse),
    buildOne(T, 'curve', true, coarse),
    buildOne(T, 'helix', false, coarse),
  ];
}

/**
 * Balconies. Deck + outer railing + return ends + brackets underneath.
 * Local frame: origin on the facade plane at the balcony's centre, deck TOP at y = 0.
 */
export function buildBalconyVariants(T, coarse = false) {
  const b = T.balcony;
  const L = T.lod;
  const widths = [2.7, 3.8, 4.9];
  return widths.map((w) => {
    const mb = new MeshBuilder(false);
    const out = b.out;
    // deck
    mb.box(0, -b.deckH * 0.5, out * 0.5, w, b.deckH, out, FACE.NO_BOTTOM, 0.6);
    // fascia band along the outer edge
    mb.box(0, -b.deckH - 0.07, out, w, 0.16, 0.07, FACE.SIDES, 0.6);

    /**
     * FAR LOD. A 4.9 m balcony carries 24 balusters = 192 of its 266 triangles, and there are
     * ~390 balconies. Past the detail radius the pickets alias to noise, so the coarse build
     * thins them out — but it does NOT close them up. See TUNING.lod.balconyBalusterSkip.
     */
    const rail = (x0, z0, x1, z1, dense) => {
      segBox(mb, x0, b.railH, z0, x1, b.railH, z1, b.railThick, b.railThick, FACE.SIDES);
      if (coarse && !dense) return;                 // return rails: top rail only
      const len = Math.sqrt((x1 - x0) * (x1 - x0) + (z1 - z0) * (z1 - z0));
      const every = (dense ? b.balusterEvery : b.balusterEvery * 1.5)
        * (coarse ? L.balconyBalusterSkip : 1);
      const n = Math.max(2, Math.round(len / every));
      for (let i = 0; i <= n; i++) {
        const t = i / n;
        const px = x0 + (x1 - x0) * t, pz = z0 + (z1 - z0) * t;
        segBox(mb, px, 0, pz, px, b.railH, pz, b.balusterThick, b.balusterThick, FACE.SIDES);
      }
    };
    rail(-w * 0.5, out, w * 0.5, out, true);
    rail(-w * 0.5, 0.02, -w * 0.5, out, false);
    rail(w * 0.5, 0.02, w * 0.5, out, false);
    // corner posts
    for (const s of [-1, 1]) {
      segBox(mb, s * w * 0.5, -0.1, out, s * w * 0.5, b.railH + 0.08, out, 0.055, 0.055, FACE.SIDES);
    }
    /**
     * Diagonal brackets back to the wall. NEAR ONLY (§9): they are 55 mm bars running UNDER the
     * deck, in the deck's own shadow, and `winter-slush` carries 221 far-LOD balconies. Sixteen
     * triangles each of geometry nobody can resolve past 74 m.
     */
    if (coarse) {
      return {
        width: w,
        geometry: mb.toGeometry('balcony:' + w + ':far'),
        tris: mb.triangleCount,
      };
    }
    for (const s of [-1, 1]) {
      segBox(mb, s * (w * 0.5 - 0.25), -b.deckH, out - 0.1, s * (w * 0.5 - 0.25), -b.deckH - 0.85, 0.05, 0.075, 0.075, FACE.SIDES);
    }
    return {
      width: w,
      geometry: mb.toGeometry('balcony:' + w + (coarse ? ':far' : '')),
      tris: mb.triangleCount,
    };
  });
}
